import React,{useState,useEffect} from 'react'
import {HashRouter as Router,Routes,Route} from 'react-router-dom'
import {motion,AnimatePresence} from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from './common/SafeIcon'
import * as localStorageService from './lib/localStorageService'
import Login from './components/Login'
import Header from './components/Header'
import InvoiceForm from './components/InvoiceForm'
import InvoiceList from './components/InvoiceList'
import PendingInvoices from './components/PendingInvoices'
import ArchivedInvoices from './components/ArchivedInvoices'
import CSVImportExport from './components/CSVImportExport'
import './App.css'

const {FiPlus,FiList,FiClock}=FiIcons

function App() {
  const [user,setUser]=useState(null)
  const [invoices,setInvoices]=useState([])
  const [archivedInvoices,setArchivedInvoices]=useState([])
  const [activeTab,setActiveTab]=useState('pending')
  const [showArchiveMenu,setShowArchiveMenu]=useState(false)
  const [showCSVModal,setShowCSVModal]=useState(false)
  const [isLoading,setIsLoading]=useState(true)
  const [error,setError]=useState(null)

  // Check for existing session
  useEffect(()=> {
    const checkSession=()=> {
      try {
        console.log('🔍 Checking localStorage session...')
        const currentUser = localStorageService.getCurrentUser()
        
        if (currentUser) {
          console.log('✅ Session found:',currentUser.email)
          setUser(currentUser)
          loadData(currentUser.id)
        } else {
          console.log('ℹ️ No session found')
          setIsLoading(false)
        }
      } catch (error) {
        console.error('❌ Session check error:',error)
        setError('Napaka pri preverjanju seje: ' + error.message)
        setIsLoading(false)
      }
    }

    checkSession()
  }, [])

  const loadData=async (userId)=> {
    try {
      console.log('📊 Loading data for:',userId)
      setIsLoading(true)
      
      // Load invoices
      const {data: invoicesData, error: invoicesError} = await localStorageService.getInvoices(userId)
      if (invoicesError) {
        console.error('❌ Invoices error:',invoicesError)
        throw new Error('Napaka pri nalaganju računov: ' + invoicesError.message)
      } else {
        setInvoices(invoicesData || [])
        console.log('📋 Loaded', invoicesData?.length || 0, 'invoices')
      }

      // Load archived invoices
      const {data: archivedData, error: archivedError} = await localStorageService.getArchivedInvoices(userId)
      if (archivedError) {
        console.error('❌ Archived error:',archivedError)
        throw new Error('Napaka pri nalaganju arhiva: ' + archivedError.message)
      } else {
        setArchivedInvoices(archivedData || [])
        console.log('📁 Loaded', archivedData?.length || 0, 'archived invoices')
      }

      setError(null)
    } catch (error) {
      console.error('❌ Error loading data:',error)
      setError(error.message || 'Napaka pri nalaganju podatkov')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogin=async (userData)=> {
    try {
      console.log('👤 User logged in:',userData.email)
      setUser(userData)
      setIsLoading(true)
      await loadData(userData.id)
    } catch (error) {
      console.error('❌ Login error:',error)
      setError('Napaka pri prijavi: ' + error.message)
      setIsLoading(false)
      throw error
    }
  }

  const handleLogout=async ()=> {
    try {
      await localStorageService.signOut()
      setUser(null)
      setInvoices([])
      setArchivedInvoices([])
      setActiveTab('pending')
      setError(null)
    } catch (error) {
      console.error('❌ Logout error:',error)
      setError('Napaka pri odjavi: ' + error.message)
    }
  }

  const addInvoice=async (invoice)=> {
    if (!user) return
    try {
      const invoiceData={
        user_id: user.id,
        stranka: invoice.stranka,
        amount: invoice.amount,
        total_amount: invoice.totalAmount,
        interval: invoice.interval,
        next_due: invoice.nextDue,
        description: invoice.description || '',
        notes: invoice.notes || '',
        line_items: invoice.lineItems || []
      }

      const {data, error} = await localStorageService.addInvoice(invoiceData)
      if (error) {
        throw new Error(error.message)
      }

      setInvoices(prev=> [...prev, data])
      setError(null)
    } catch (error) {
      console.error('❌ Error adding invoice:',error)
      setError('Napaka pri dodajanju računa: ' + error.message)
    }
  }

  const markAsPaid=async (invoiceId)=> {
    if (!user) return
    try {
      const invoice = invoices.find(inv=> inv.id===invoiceId)
      if (!invoice) return

      const currentDueDate = new Date(invoice.next_due || invoice.nextDue)
      const nextDue = calculateNextDueDate(currentDueDate, invoice.interval)
      const updateData={
        last_paid: new Date().toISOString(),
        next_due: nextDue.toISOString()
      }

      const {data, error} = await localStorageService.updateInvoice(invoiceId, updateData)
      if (error) {
        throw new Error(error.message)
      }

      setInvoices(prev=> prev.map(inv=> inv.id===invoiceId ? data : inv))
      setError(null)
    } catch (error) {
      console.error('❌ Error marking as paid:',error)
      setError('Napaka pri beleženju plačila: ' + error.message)
    }
  }

  const calculateNextDueDate=(currentDate, interval)=> {
    const date = new Date(currentDate)
    switch (interval) {
      case 'monthly': 
        date.setMonth(date.getMonth() + 1)
        break
      case 'quarterly': 
        date.setMonth(date.getMonth() + 3)
        break
      case 'half-yearly': 
        date.setMonth(date.getMonth() + 6)
        break
      case 'yearly': 
        date.setFullYear(date.getFullYear() + 1)
        break
      default: 
        date.setMonth(date.getMonth() + 1)
    }
    return date
  }

  const archiveInvoice=async (invoiceId)=> {
    if (!user) return
    try {
      const invoiceToArchive = invoices.find(inv=> inv.id===invoiceId)
      if (!invoiceToArchive) return

      const archivedData={
        user_id: user.id,
        original_invoice_id: invoiceToArchive.id,
        stranka: invoiceToArchive.stranka,
        amount: invoiceToArchive.amount,
        total_amount: invoiceToArchive.total_amount || invoiceToArchive.totalAmount,
        interval: invoiceToArchive.interval,
        next_due: invoiceToArchive.next_due || invoiceToArchive.nextDue,
        description: invoiceToArchive.description,
        notes: invoiceToArchive.notes,
        line_items: invoiceToArchive.line_items || invoiceToArchive.lineItems,
        last_paid: invoiceToArchive.last_paid,
        archived_at: new Date().toISOString()
      }

      const {data: archived, error: archiveError} = await localStorageService.addArchivedInvoice(archivedData)
      if (archiveError) {
        throw new Error(archiveError.message)
      }

      const {error: deleteError} = await localStorageService.deleteInvoice(invoiceId)
      if (deleteError) {
        throw new Error(deleteError.message)
      }

      setArchivedInvoices(prev=> [...prev, archived])
      setInvoices(prev=> prev.filter(inv=> inv.id !== invoiceId))
      setError(null)
    } catch (error) {
      console.error('❌ Error archiving invoice:',error)
      setError('Napaka pri arhiviranju računa: ' + error.message)
    }
  }

  const restoreInvoice=async (invoiceId)=> {
    if (!user) return
    try {
      const invoiceToRestore = archivedInvoices.find(inv=> inv.id===invoiceId)
      if (!invoiceToRestore) return

      const invoiceData={
        user_id: user.id,
        stranka: invoiceToRestore.stranka,
        amount: invoiceToRestore.amount,
        total_amount: invoiceToRestore.total_amount,
        interval: invoiceToRestore.interval,
        next_due: invoiceToRestore.next_due,
        description: invoiceToRestore.description,
        notes: invoiceToRestore.notes,
        line_items: invoiceToRestore.line_items,
        last_paid: invoiceToRestore.last_paid
      }

      const {data: restored, error: restoreError} = await localStorageService.addInvoice(invoiceData)
      if (restoreError) {
        throw new Error(restoreError.message)
      }

      const {error: deleteError} = await localStorageService.deleteArchivedInvoice(invoiceId)
      if (deleteError) {
        throw new Error(deleteError.message)
      }

      setInvoices(prev=> [...prev, restored])
      setArchivedInvoices(prev=> prev.filter(inv=> inv.id !== invoiceId))
      setError(null)
    } catch (error) {
      console.error('❌ Error restoring invoice:',error)
      setError('Napaka pri obnavljanju računa: ' + error.message)
    }
  }

  const permanentDeleteInvoice=async (invoiceId)=> {
    if (!user) return
    try {
      const {error} = await localStorageService.deleteArchivedInvoice(invoiceId)
      if (error) {
        throw new Error(error.message)
      }

      setArchivedInvoices(prev=> prev.filter(inv=> inv.id !== invoiceId))
      setError(null)
    } catch (error) {
      console.error('❌ Error permanently deleting invoice:',error)
      setError('Napaka pri brisanju računa: ' + error.message)
    }
  }

  const editInvoice=async (invoiceId, updatedInvoice)=> {
    if (!user) return
    try {
      const updateData={
        stranka: updatedInvoice.stranka,
        amount: updatedInvoice.amount,
        total_amount: updatedInvoice.totalAmount,
        interval: updatedInvoice.interval,
        next_due: updatedInvoice.nextDue,
        description: updatedInvoice.description,
        notes: updatedInvoice.notes,
        line_items: updatedInvoice.lineItems
      }

      const {data, error} = await localStorageService.updateInvoice(invoiceId, updateData)
      if (error) {
        throw new Error(error.message)
      }

      setInvoices(prev=> prev.map(invoice=> invoice.id===invoiceId ? data : invoice))
      setError(null)
    } catch (error) {
      console.error('❌ Error editing invoice:',error)
      setError('Napaka pri urejanju računa: ' + error.message)
    }
  }

  const handleCSVImport=async (importedInvoices)=> {
    if (!user) return
    try {
      console.log('📥 Starting CSV import with',importedInvoices.length,'invoices')
      const validInvoices = importedInvoices.filter(invoice=> {
        return invoice.stranka && invoice.stranka.trim() !==''
      })

      if (validInvoices.length===0) {
        throw new Error('Ni veljavnih računov za uvoz')
      }

      const newInvoices = []
      for (const invoice of validInvoices) {
        const invoiceData={
          user_id: user.id,
          stranka: invoice.stranka,
          amount: parseFloat(invoice.amount) || 0,
          total_amount: parseFloat(invoice.totalAmount) || parseFloat(invoice.amount) || 0,
          interval: invoice.interval || 'monthly',
          next_due: invoice.nextDue || new Date().toISOString(),
          description: invoice.description || '',
          notes: invoice.notes || '',
          line_items: Array.isArray(invoice.lineItems) ? invoice.lineItems : []
        }

        const {data, error} = await localStorageService.addInvoice(invoiceData)
        if (error) {
          throw new Error(error.message)
        }
        newInvoices.push(data)
      }

      setInvoices(prev=> [...prev, ...newInvoices])
      setError(null)
      alert(`Uspešno uvoženih ${validInvoices.length} računov!`)
    } catch (error) {
      console.error('❌ Error importing invoices:',error)
      setError('Napaka pri uvozu računov: ' + error.message)
    }
  }

  const getPendingInvoices = ()=> {
    try {
      const today = new Date()
      return invoices.filter(invoice=> {
        const dueDate = new Date(invoice.next_due || invoice.nextDue)
        return dueDate <= today
      })
    } catch (error) {
      console.error('❌ Error getting pending invoices:',error)
      return []
    }
  }

  const mainTabs = [
    {id: 'pending', label: 'V plačilo', icon: FiClock},
    {id: 'add', label: 'Dodaj', icon: FiPlus},
    {id: 'all', label: 'Vsi računi', icon: FiList}
  ]

  // Loading screen with better error handling
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6e725c] mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-[#4d504a] mb-2">Nalagam aplikacijo</h2>
            <p className="text-[#6e725c] text-sm mb-4">
              Pripravljam aplikacijo...
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                <p className="font-medium">Napaka pri nalaganju:</p>
                <p className="text-xs mt-1">{error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="mt-2 bg-red-600 text-white px-4 py-2 rounded text-xs hover:bg-red-700"
                >
                  Ponovno naloži
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // If user is not logged in, show login page
  if (!user) {
    return <Login onLogin={handleLogin} />
  }

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container mx-auto px-4 py-6">
          <Header
            user={user}
            onLogout={handleLogout}
            archivedInvoices={archivedInvoices}
            showArchiveMenu={showArchiveMenu}
            setShowArchiveMenu={setShowArchiveMenu}
            setActiveTab={setActiveTab}
            onOpenCSV={()=> setShowCSVModal(true)}
          />

          <div className="max-w-7xl mx-auto">
            <nav className="flex justify-center mb-6">
              <div className="bg-white rounded-lg shadow-md p-1 flex space-x-1">
                {mainTabs.map((tab)=> (
                  <button
                    key={tab.id}
                    onClick={()=> setActiveTab(tab.id)}
                    className={`flex items-center px-4 py-2 rounded-md transition-all duration-200 text-sm font-medium ${
                      activeTab===tab.id
                        ? 'bg-[#6e725c] text-white shadow-sm'
                        : 'text-[#4d504a] hover:bg-gray-100'
                    }`}
                  >
                    <SafeIcon icon={tab.icon} className="mr-1.5 text-sm" />
                    {tab.label}
                    {tab.id==='pending' && getPendingInvoices().length > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                        {getPendingInvoices().length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </nav>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm max-w-4xl mx-auto"
              >
                <div className="flex justify-between items-center">
                  <span>{error}</span>
                  <button
                    onClick={()=> setError(null)}
                    className="ml-4 text-red-500 hover:text-red-700"
                  >
                    ✕
                  </button>
                </div>
              </motion.div>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {activeTab==='pending' && (
                  <PendingInvoices
                    invoices={getPendingInvoices()}
                    onMarkAsPaid={markAsPaid}
                  />
                )}

                {activeTab==='add' && (
                  <InvoiceForm onAddInvoice={addInvoice} />
                )}

                {activeTab==='all' && (
                  <InvoiceList
                    invoices={invoices}
                    onDeleteInvoice={archiveInvoice}
                    onEditInvoice={editInvoice}
                    onMarkAsPaid={markAsPaid}
                  />
                )}

                {activeTab==='archive' && (
                  <ArchivedInvoices
                    archivedInvoices={archivedInvoices}
                    onRestoreInvoice={restoreInvoice}
                    onPermanentDelete={permanentDeleteInvoice}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* CSV Import/Export Modal */}
        <AnimatePresence>
          {showCSVModal && (
            <CSVImportExport
              invoices={invoices}
              onImport={handleCSVImport}
              onClose={()=> setShowCSVModal(false)}
            />
          )}
        </AnimatePresence>
      </div>
    </Router>
  )
}

export default App